<script setup lang="ts">
import { computed, ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import { getPropertyDisplayTitle, getPropertyPartialTask, getPropertyValueFromTask } from "~/common/properties";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import OptionComponent from "~/components/options/Option.vue";
import OptionDropdownItem from "~/components/options/OptionDropdownItem.vue";
import { UNSET_PROPERTY_PSEUDO_VALUE } from "~/constants/pseudoDuids";
import { colorsByTheme } from "~/constants/style";
import { PlusIcon, SelectFieldIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import { EditorMode, Placement } from "~/shared/enums";
import type { PropertySelect, PropertyValueForKind, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { makeValidateOptionTitle } from "~/utils/validation";

type Value = PropertyValueForKind<PropertySelect>;

const props = defineProps<{
  property: PropertySelect;
  tasks: Task[];
  defaultValue?: Value;
  editorMode: EditorMode;
  value?: Value;
  onAfterClose?: (value: Value) => void;
}>();

const emit = defineEmits<{
  update: [value?: Value];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);
const dropdownMenu = ref<ComponentExposed<typeof MultiselectDropdownMenu> | null>(null);

const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isChipMode = computed(() => props.editorMode === EditorMode.CHIP);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(() => isFormMode.value || isAutomationMode.value);

const selectedTasks = computed(() =>
  !isListMode.value
    ? props.tasks
    : dataStore.getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
        includeTrashed: false,
        includeDraft: false,
      })
);

const optionDuids = computed(() => {
  if (isFormOrAutomationMode.value) {
    return props.value ? [props.value] : [];
  }

  return [...new Set(props.tasks.map((e) => getPropertyValueFromTask(props.property, e)))].filter(isDefined);
});
const isUnsetSelected = computed(() => props.value === UNSET_PROPERTY_PSEUDO_VALUE);

const optionSections = computed(() => {
  const selectedOptionDuids = isFormOrAutomationMode.value
    ? props.value
      ? [props.value]
      : []
    : [...new Set(selectedTasks.value.map((e) => getPropertyValueFromTask(props.property, e)))];

  return [
    {
      title: "Options",
      items: dataStore.getOptionList(props.property).map((option) => {
        const selected = selectedOptionDuids.includes(option.duid);
        return {
          value: option.duid,
          label: option.title,
          selected,
          component: OptionDropdownItem,
          componentArgs: { option, selected },
        };
      }),
    },
  ];
});

const activeOptions = computed(() =>
  optionDuids.value
    .filter(isDefined)
    .map((duid) => dataStore.getOptionByDuid(duid))
    .filter(isDefined)
);

const updateTasks = (value: Value) => {
  if (isFormOrAutomationMode.value) {
    emit("update", value);
    return;
  }

  dataStore.updateTasks(
    selectedTasks.value.map((task) => ({
      duid: task.duid,
      ...getPropertyPartialTask(props.property, task, value),
    }))
  );

  emit("update");
};

const onAdd = (optionDuid: Value) => {
  updateTasks(optionDuid);
};

const onRemove = () => {
  updateTasks(null);
};

// TODO transactionify
const onCreate = async (optionDuid: Exclude<Value, null>) => {
  const option = await dataStore.createOption(optionDuid, props.property);
  if (!option) {
    return;
  }

  updateTasks(option.duid);
};

const validateOptionTitle = computed(() =>
  makeValidateOptionTitle((title) => dataStore.getOptionByTitle(title, props.property, null))
);

const handleAfterClose = () => {
  props.onAfterClose?.(optionDuids.value[0]);
};

defineExpose({
  open: () => dropdownMenu.value?.open(),
  isOpen: computed(() => dropdownMenu.value?.isOpen),
});
</script>

<template>
  <MultiselectDropdownMenu
    ref="dropdownMenu"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :sections="optionSections"
    placeholder="Add an option..."
    :header-title="`Set ${getPropertyDisplayTitle(property).toLowerCase()}`"
    :block="isFormMode"
    :height-block="isFormOrAutomationMode"
    :cover="!isContextMenuMode && !isFormOrAutomationMode"
    :distance="isFormOrAutomationMode ? 2 : undefined"
    :skidding="isFormOrAutomationMode ? -1 : undefined"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :enable-new-entry="!pageStore.isPublicForm"
    :validate="validateOptionTitle"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }"
    :on-after-close="handleAfterClose"
    close-on-select
    @remove="onRemove"
    @add="onAdd"
    @create="onCreate">
    <DropdownMenuItemContent
      v-if="isContextMenuMode"
      :icon="SelectFieldIcon"
      :title="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
    <Tooltip
      v-else
      :disabled="isFormOrAutomationMode"
      :text="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      :block="isListMode || isFormOrAutomationMode"
      :height-block="isListMode || isFormOrAutomationMode"
      class="w-full">
      <div
        v-if="isChipMode"
        class="flex items-center gap-1 rounded-sm border border-oncolor px-1 py-0.5 text-md hover:bg-md">
        <SelectFieldIcon
          :class="{
            'text-primary-base': isChipMode && optionDuids.length !== 0,
          }"
          class="icon-sm" />
        <span v-if="optionDuids.length === 0" class="max-w-xs truncate select-none">
          {{ getPropertyDisplayTitle(property) }}
        </span>
        <template v-else>
          <OptionComponent
            v-for="option in activeOptions"
            :key="option.duid"
            :option="option"
            show-ancestors
            truncate
            class="max-w-xs" />
        </template>
      </div>

      <div
        v-else
        class="group/select flex size-full cursor-pointer items-center gap-1"
        :class="{
          'overflow-x-hidden': isListMode,
          'flex-wrap rounded py-0.5': !isListMode,
          'hover:bg-md': !isListMode && !isTaskDetailMode && !isFormOrAutomationMode,
          'px-1': activeOptions.length !== 0,
          'justify-start px-2 py-1 hover:bg-lt': isTaskDetailMode,
          'truncate px-2 text-sm hover:bg-lt': isFormOrAutomationMode,
        }">
        <OptionComponent
          v-for="option in activeOptions"
          :key="option.duid"
          show-ancestors
          :option="option"
          :truncate="!isTaskDetailMode" />
        <template v-if="!isListMode && !activeOptions.length">
          <div v-if="isTaskDetailMode || isFormOrAutomationMode" class="truncate text-left text-vlt select-none">
            {{
              isUnsetSelected
                ? "(Unset)"
                : isTaskDetailMode
                  ? "None"
                  : `Set ${getPropertyDisplayTitle(property).toLowerCase()}`
            }}
          </div>
          <PlusIcon v-else class="icon-sm" />
        </template>

        <div
          v-if="isListMode && !activeOptions.length"
          class="flex size-full items-center justify-center text-transparent group-hover/select:text-vlt">
          <SelectFieldIcon class="icon-xs" />
        </div>
      </div>
    </Tooltip>
  </MultiselectDropdownMenu>
</template>
